import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Box, CloseButton, ConfirmationModal, IconButton, Row, TextField, Typography } from 'seagull';
import { useOverlayContext } from 'seagull/components/OverlayContext';
import { Eye } from 'seagull/icons/essentials';
import { getTestIds } from 'seagull/utils/TestIds';
import VideoPlayer, { VideoPlayerStatus } from '~/components/VideoPlayer/VideoPlayer';
import { useLocaleKeys } from '~/i18n';
import { isUrlValid } from '~/utils/videoUrl/videoUrl';
export const GetVideoUrlInputTID = (parentTestId)=>getTestIds(parentTestId);
export const VideoUrlInput = (param)=>{
    let { value, onChange, onBlur, required, helperText, showPreviewButton, disabled, submitAttempted, testId, error: externalError, errorMessage: externalErrorMessage, autoFocus } = param;
    const { t } = useLocaleKeys();
    const testIds = useMemo(()=>GetVideoUrlInputTID(testId), [
        testId
    ]);
    const { showModal } = useOverlayContext();
    const [hasBlurred, setHasBlurred] = useState(false);
    const handleBlur = ()=>{
        setHasBlurred(true);
        onBlur === null || onBlur === void 0 ? void 0 : onBlur();
    };
    const invalidKind = value === '' && required ? 'empty' : value !== '' && !isUrlValid(value) ? 'bad-format' : 'none';
    const internalShouldShowError = !disabled && invalidKind !== 'none' && (hasBlurred || submitAttempted === true);
    const shouldShowError = externalError === true || internalShouldShowError;
    const internalErrorText = invalidKind === 'empty' ? helperText !== null && helperText !== void 0 ? helperText : t.customPages.videoUrl.description() : invalidKind === 'bad-format' ? t.customPages.videoUrl.errors.notFound() : undefined;
    const errorText = externalErrorMessage !== null && externalErrorMessage !== void 0 ? externalErrorMessage : internalErrorText;
    const idleHelperText = value === '' ? helperText !== null && helperText !== void 0 ? helperText : t.customPages.videoUrl.description() : undefined;
    const displayedText = shouldShowError ? errorText : idleHelperText;
    const isPreviewEnabled = showPreviewButton === true && isUrlValid(value);
    const handlePreviewClick = ()=>{
        if (!isPreviewEnabled) return;
        showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            biName: 'video_url_preview_modal',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(PreviewModalContent, {
                    url: value,
                    dismissModal: dismissModal,
                    previewModalTestId: testIds.VideoUrlInputTestIds.PreviewModal(),
                    previewModalErrorTestId: testIds.VideoUrlInputTestIds.PreviewModalError()
                });
            }
        });
    };
    const handleChange = (e)=>{
        onChange(e.target.value);
    };
    const handleClear = ()=>{
        onChange('');
    };
    const showClearButton = value !== '' && !disabled;
    const clearSuffix = showClearButton ? [
        /*#__PURE__*/ _jsx(CloseButton, {
            skin: "transparent",
            size: "small",
            onClick: handleClear,
            bi: {
                name: 'video_url_clear'
            },
            testId: testIds.VideoUrlInputTestIds.ClearButton()
        }, "clear")
    ] : undefined;
    return /*#__PURE__*/ _jsxs(Row, {
        testId: testIds.VideoUrlInputTestIds.Root(),
        gap: 8,
        alignItems: "start",
        style: {
            width: '100%'
        },
        children: [
            /*#__PURE__*/ _jsx(Box, {
                style: {
                    flex: 1
                },
                children: /*#__PURE__*/ _jsx(TextField, {
                    testId: testIds.VideoUrlInputTestIds.TextField(),
                    label: t.customPages.videoUrl.label(),
                    value: value,
                    onChange: handleChange,
                    onBlur: handleBlur,
                    error: shouldShowError,
                    helperText: displayedText,
                    disabled: disabled,
                    suffix: clearSuffix,
                    collapseHelperText: false,
                    autoFocus: autoFocus,
                    bi: {
                        name: 'video_url'
                    }
                })
            }),
            showPreviewButton && /*#__PURE__*/ _jsx(IconButton, {
                skin: "transparent",
                size: "smallMedium",
                onClick: handlePreviewClick,
                disabled: !isPreviewEnabled,
                bi: {
                    name: 'video_url_preview'
                },
                testId: testIds.VideoUrlInputTestIds.PreviewButton(),
                tooltip: !isPreviewEnabled ? {
                    placement: 'top',
                    title: t.customPages.videoUrl.previewDisabledTooltip()
                } : undefined,
                style: {
                    marginTop: 8
                },
                children: /*#__PURE__*/ _jsx(Eye, {
                    size: 24
                })
            })
        ]
    });
};
const PreviewModalContent = (param)=>{
    let { url, dismissModal, previewModalTestId, previewModalErrorTestId } = param;
    const { t } = useLocaleKeys();
    const [playerError, setPlayerError] = useState(undefined);
    const handleError = (status)=>{
        setPlayerError(status);
    };
    const errorMessage = playerError === VideoPlayerStatus.NOT_FOUND ? t.customPages.videoUrl.errors.notFound() : playerError === VideoPlayerStatus.ACCESS_DENIED ? t.customPages.videoUrl.errors.accessDenied() : undefined;
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        title: t.customPages.videoUrl.previewModalTitle(),
        onCloseButtonClick: ()=>dismissModal(),
        width: "640px",
        testId: previewModalTestId,
        content: /*#__PURE__*/ _jsxs(Box, {
            children: [
                /*#__PURE__*/ _jsx(VideoPlayer, {
                    url: url,
                    onError: handleError
                }),
                errorMessage != null && /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraph2",
                    skin: "error",
                    testId: previewModalErrorTestId,
                    children: errorMessage
                })
            ]
        })
    });
};
